import type {
  AdminClient,
  AdminConfig,
  AdminUser,
  ClientScope,
  CreatedClientSecret,
  EntityStatus,
  TransferClientOption,
  TransferLogPage,
  TransferOp,
  TransferSortDirection,
  TransferSortKey,
  TransferStatus,
} from '@baex/shared';
import { api, buildQuery } from './client';

const BASE = '/admin/api';

export interface AdminIdentity {
  email: string;
  displayName: string;
}

export interface DashboardSummary {
  clients: number;
  activeClients: number;
  filesWaiting: number;
  bytesWaiting: number;
  diskFreeBytes: number;
  errorsLast24h: number;
  /** The same errors, split the way the table labels them. */
  clientFaultsLast24h: number;
  serverFaultsLast24h: number;
  uploadsLast24h: number;
  downloadsLast24h: number;
}

/** Filters accepted by `GET /admin/api/transfers` (functional spec, API Contract Reference — Transfer history). */
export interface TransferFilters {
  client?: string;
  /**
   * Present-or-absent, not truthy-or-falsy (functional spec, API Contract Reference — Transfer history): `''` is sent as
   * `subfolder=` and filters to the folder root, while `undefined` omits the parameter
   * and applies no subfolder filter at all.
   */
  subfolder?: string;
  op?: TransferOp;
  /**
   * One of the five *stored* status values (see the "Table" section). `error` is the coarse switch:
   * it matches both `error (client)` and `error (server)` rows, since that split is a
   * display mapping from the error code and never a stored value of its own.
   */
  status?: TransferStatus;
  from?: string;
  to?: string;
  q?: string;
  limit?: number;
  offset?: number;
  /** Sorting is applied by the server, because the table is lazily loaded. */
  sort?: TransferSortKey;
  direction?: TransferSortDirection;
}

export const auth = {
  login: (email: string, password: string) =>
    api.post<AdminIdentity>(`${BASE}/auth/login`, { email, password }, { skipAuthRedirect: true }),
  logout: () => api.post<void>(`${BASE}/auth/logout`),
  me: () => api.get<AdminIdentity>(`${BASE}/auth/me`),
};

export const admins = {
  list: () => api.get<AdminUser[]>(`${BASE}/admins`),
  create: (body: { email: string; displayName: string; password: string }) =>
    api.post<AdminUser>(`${BASE}/admins`, body),
  setStatus: (email: string, status: EntityStatus) =>
    api.patch<AdminUser>(`${BASE}/admins/${encodeURIComponent(email)}`, { status }),
  setPassword: (email: string, password: string) =>
    api.post<void>(`${BASE}/admins/${encodeURIComponent(email)}/password`, { password }),
  remove: (email: string) => api.delete<void>(`${BASE}/admins/${encodeURIComponent(email)}`),
};

export const clients = {
  list: () => api.get<AdminClient[]>(`${BASE}/clients`),
  create: (body: { clientId: string; displayName: string; scope: ClientScope; notes?: string }) =>
    api.post<{ client: AdminClient; secret: CreatedClientSecret }>(`${BASE}/clients`, body),
  // The API also accepts `notes` on PATCH, but editing notes is not part of this
  // version's UI (see the "Client list — table and row actions" section), so the typed client deliberately cannot send it.
  update: (clientId: string, body: { displayName?: string; status?: EntityStatus }) =>
    api.patch<AdminClient>(`${BASE}/clients/${encodeURIComponent(clientId)}`, body),
  rotateKey: (clientId: string) =>
    api.post<CreatedClientSecret>(`${BASE}/clients/${encodeURIComponent(clientId)}/rotate-key`),
  remove: (clientId: string) =>
    api.delete<void>(`${BASE}/clients/${encodeURIComponent(clientId)}`, {
      confirmClientId: clientId,
    }),
};

export const transfers = {
  list: (filters: TransferFilters, signal?: AbortSignal) =>
    api.get<TransferLogPage>(`${BASE}/transfers${buildQuery({ ...filters })}`, signal),
  /**
   * Every Client ID the history mentions, including clients since deleted — the Transfers
   * filter must still reach their rows (functional spec, API Contract Reference — Transfer history; and Summary strip).
   */
  clientOptions: () => api.get<TransferClientOption[]>(`${BASE}/transfers/clients`),
  /**
   * Distinct subfolders the history mentions, optionally scoped to one client. The empty
   * string is a real entry - it is the folder root - and the list is drawn from the whole
   * history rather than from the rows on screen, so narrowing the table cannot narrow the
   * options with it (functional spec, API Contract Reference — Transfer history).
   */
  subfolderOptions: (client?: string) =>
    api.get<string[]>(`${BASE}/transfers/subfolders${buildQuery({ client })}`),
};

export const config = {
  get: () => api.get<AdminConfig>(`${BASE}/config`),
};

export const summary = {
  get: () => api.get<DashboardSummary>(`${BASE}/summary`),
};
